/**
 * Fotografije sa stanice — red na telefonu i slanje.
 *
 * Analiza 2.0 (D6), 14.10.2026: CLAUDE.md traži da vozač bez mreže radi i
 * fotografije, a nisu postojale. Vlasnik: „radi".
 *
 * Slika NE ide u red zapisa (`outbox.ts`): taj red je u localStorage, koji
 * prima nekoliko megabajta i napunio bi se posle desetak slika — a onda ni
 * štrik ne bi mogao da se sačuva. Slike zato stoje u IndexedDB-u i šalju se
 * svojim pozivom, jedna po jedna, kad ima signala.
 */

export interface FotoZapis {
  /** Broj koji pravi telefon; server po njemu prepoznaje ponovljeno slanje. */
  id: string;
  tourId: number;
  accessKey: string;
  stopId: number;
  /** Lokalno vreme telefona, kao kod ostalih zapisa vozača. */
  takenAt: string;
  note?: string;
  /** Smanjena slika, „data:image/jpeg;base64,…". */
  image: string;
}

/** Gde red stoji. Zamenljivo, da se slanje testira bez pregledača. */
export interface FotoSkladiste {
  sve(): Promise<FotoZapis[]>;
  sacuvaj(zapis: FotoZapis): Promise<void>;
  obrisi(id: string): Promise<void>;
}

const BAZA = 'elbraco-vozac';
const SKLADISTE = 'fotografije';

function otvori(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    if (typeof indexedDB === 'undefined') {
      reject(new Error('Telefon ne može da čuva fotografije bez mreže.'));
      return;
    }

    const zahtev = indexedDB.open(BAZA, 1);
    zahtev.onupgradeneeded = () => {
      if (!zahtev.result.objectStoreNames.contains(SKLADISTE)) {
        zahtev.result.createObjectStore(SKLADISTE, { keyPath: 'id' });
      }
    };
    zahtev.onsuccess = () => resolve(zahtev.result);
    zahtev.onerror = () => reject(zahtev.error ?? new Error('Skladište fotografija se ne otvara.'));
  });
}

function uradi<T>(
  nacin: IDBTransactionMode, posao: (s: IDBObjectStore) => IDBRequest<T>
): Promise<T> {
  return otvori().then((baza) => new Promise<T>((resolve, reject) => {
    const tx = baza.transaction(SKLADISTE, nacin);
    const zahtev = posao(tx.objectStore(SKLADISTE));
    tx.oncomplete = () => { baza.close(); resolve(zahtev.result); };
    tx.onerror = () => { baza.close(); reject(tx.error ?? new Error('Fotografija nije sačuvana.')); };
  }));
}

/** Pravo skladište na telefonu. */
export const telefonskoSkladiste: FotoSkladiste = {
  sve: () => uradi<FotoZapis[]>('readonly', (s) => s.getAll() as IDBRequest<FotoZapis[]>),
  sacuvaj: (zapis) => uradi('readwrite', (s) => s.put(zapis)).then(() => undefined),
  obrisi: (id) => uradi('readwrite', (s) => s.delete(id)).then(() => undefined)
};

/** Mere slike posle smanjenja: duža strana najviše `najvise`, odnos ostaje. */
export function meraSmanjenja(sirina: number, visina: number, najvise = 1600): { w: number; h: number } {
  const duza = Math.max(sirina, visina);
  if (duza <= najvise || duza === 0) return { w: sirina, h: visina };

  const k = najvise / duza;
  return { w: Math.round(sirina * k), h: Math.round(visina * k) };
}

/**
 * Smanjuje fotografiju sa kamere (3–5 MB) na oko 200 KB. Slanje preko slabog
 * signala i skladište na telefonu to traže; za dokaz o oštećenju je dovoljno.
 */
export function smanji(fajl: File, najvise = 1600, kvalitet = 0.75): Promise<string> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(fajl);
    const slika = new Image();

    slika.onload = () => {
      const { w, h } = meraSmanjenja(slika.naturalWidth, slika.naturalHeight, najvise);
      const platno = document.createElement('canvas');
      platno.width = w;
      platno.height = h;
      const ctx = platno.getContext('2d');
      URL.revokeObjectURL(url);

      if (!ctx) {
        reject(new Error('Telefon ne može da obradi sliku.'));
        return;
      }

      ctx.drawImage(slika, 0, 0, w, h);
      resolve(platno.toDataURL('image/jpeg', kvalitet));
    };
    slika.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Slika se ne da pročitati.'));
    };
    slika.src = url;
  });
}

export interface IshodSlanja {
  poslato: number;
  /** Slike koje server trajno nije primio — vozač mora da javi šefu. */
  odbijeno: { zapis: FotoZapis; razlog: string }[];
}

/**
 * Šalje sve slike iz reda, jednu po jednu.
 *
 * - primljeno (i ponovljeno) — skida se iz reda;
 * - odbijeno (400, 404, 422) — skida se i vraća sa razlogom: ponovno slanje
 *   tu ne pomaže, isto kao kod odbijenog zapisa;
 * - nema signala ili server ne radi — ostaje i staje se; ostale bi pale isto.
 */
export async function posalji(
  skladiste: FotoSkladiste, base: string, fetchFn: typeof fetch = fetch, tekuciKljuc?: string
): Promise<IshodSlanja> {
  const ishod: IshodSlanja = { poslato: 0, odbijeno: [] };

  for (const zapis of await skladiste.sve()) {
    const salji = (kljuc: string) => fetchFn(
      `${base}/api/driver/tours/${zapis.tourId}/stops/${zapis.stopId}/fotografije`
        + `?kljuc=${encodeURIComponent(kljuc)}`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          clientPhotoId: zapis.id, takenAt: zapis.takenAt, note: zapis.note, image: zapis.image
        })
      });

    let odgovor: Response;

    try {
      odgovor = await salji(zapis.accessKey);
    } catch {
      break;
    }

    // SLIKA SA LINKOM KOJI JE ŠEF ZAMENIO (analiza scenarija, K5). Šalje se
    // ključem iz linka koji je vozač otvorio; server proverava da je tura
    // njegova. Kad ni tako ne prođe (tuđa tura na deljenom telefonu, ili ni
    // novi link ne važi), slika ostaje — a OSTALE slike idu dalje: do tada je
    // jedna takva zaustavljala slanje svih.
    if (odgovor.status === 401 && tekuciKljuc && tekuciKljuc !== zapis.accessKey) {
      try {
        odgovor = await salji(tekuciKljuc);
      } catch {
        break;
      }

      if (!odgovor.ok && odgovor.status !== 400 && odgovor.status !== 422) {
        continue;
      }
    }

    if (odgovor.ok) {
      await skladiste.obrisi(zapis.id);
      ishod.poslato++;
      continue;
    }

    if (odgovor.status === 400 || odgovor.status === 404 || odgovor.status === 422) {
      const razlog = (await odgovor.text().catch(() => '')).replace(/^"|"$/g, '')
        || `Server nije primio fotografiju (${odgovor.status}).`;
      await skladiste.obrisi(zapis.id);
      ishod.odbijeno.push({ zapis, razlog });
      continue;
    }

    // 401 (link ne važi), 5xx, 413 i ostalo: slika ostaje, vozač je vidi kao
    // neposlatu, i pokušava se ponovo.
    break;
  }

  return ishod;
}
